/**
 * Getting a brand image back out again (2026-09-30).
 *
 * A logo is uploaded once and then needed everywhere — a sign painter, a
 * business card, an invoice template. The file lives in Supabase storage under
 * a uuid name, so "right-click, save as" hands you
 * `3f5c2774-b54e-48d7-aff7-5f32f1100f08.png`, which is not a thing anyone can
 * find again.
 *
 * ⚠️ AND `download` ALONE DOES NOT WORK HERE. The `download` attribute is
 * ignored for a cross-origin href, and storage is a different origin from the
 * app — the browser would navigate to the image instead of saving it. So the
 * bytes are fetched first and saved from a blob URL, with opening the image in
 * a tab as the fallback when that fetch is refused.
 */

const EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif']);

/**
 * A filename a person can find later: `tonie-searle-designs-logo.png`.
 *
 * The extension comes from the stored path, because that is what the file
 * actually is — the name it was uploaded under is long gone.
 */
export function brandImageFilename(
  companyName: string,
  kind: 'logo' | 'photo',
  storagePath: string | null | undefined,
): string {
  const slug = companyName
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  const dot = (storagePath ?? '').lastIndexOf('.');
  const raw = dot > 0 ? (storagePath ?? '').slice(dot + 1).toLowerCase() : '';
  const ext = EXTENSIONS.has(raw) ? raw : 'png';
  return `${slug || 'brand'}-${kind}.${ext}`;
}

/**
 * Save a remote image to the visitor's computer.
 *
 * Resolves with what happened, so the caller can say something true: a blocked
 * fetch opens the image in a tab, which is a worse outcome than a download but
 * a much better one than a button that appears to do nothing.
 */
export async function downloadImage(url: string, filename: string): Promise<'saved' | 'opened'> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.append(a);
    a.click();
    a.remove();
    // Revoked on the next turn: revoking synchronously can beat the click in
    // Safari, which then saves nothing at all.
    setTimeout(() => URL.revokeObjectURL(href), 0);
    return 'saved';
  } catch {
    window.open(url, '_blank', 'noopener');
    return 'opened';
  }
}
